import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
// import { ErrorBoundary } from "react-error-boundary";
// import ErrorFallback from "./components/ErrorFallback";
import { lazy, Suspense } from "react";
import { useAuthContext } from "./hooks/useAuthContext";
import Loading from "./components/Loading";
import NotFound from "./pages/notFound/NotFound";
import Unauthorized from "./pages/notFound/Unauthorized";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import Module from "./pages/home/module/Module";
import IsAuthorized from "./components/IsAuthorized";
import { useAuthorization } from "./hooks/useAuthorization";
//import ConnectionStatus from "./components/ConnectionStatus";

const Message = lazy(() => import("./pages/message/Message"));
const Layout = lazy(() => import("./components/layout/Layout"));
const Club = lazy(() => import("./pages/club/club/Club"));
const Profile = lazy(() => import("./pages/profile/profile/Profile"));
const ForgotPassword = lazy(() =>
  import("./pages/forgotPassword/ForgotPassword")
);
const Login = lazy(() => import("./pages/login/Login"));
const Signup = lazy(() => import("./pages/signup/Signup"));
const Notification = lazy(() => import("./pages/notification/Notification"));
const LoboEco = lazy(() => import("./components/dashboard/LoboEco"));
const LoboXSell = lazy(() => import("./pages/loboXsell/LoboXSell"));
const ResetPassword = lazy(() => import("./pages/resetPassword/ResetPassword"));
const ManagerCode = lazy(() =>
  import("./pages/managerCodes/managerCode/ManagerCode")
);
const WhatsApp = lazy(() => import("./pages/whatsApp/whatsApp/WhatsApp"));
const UserRoles = lazy(() => import("./pages/profile/userRoles/UserRoles"));

const ProfileDetails = lazy(() =>
  import("./pages/profile/profileDetails/ProfileDetails")
);
const Password = lazy(() => import("./pages/profile/password/Password"));

const MiluzProfile = lazy(() => import("./pages/profile/miluz/MiluzLogin"));
const WhatsappQR = lazy(() => import("./pages/profile/whatsapp/Whatsapp"));

const VirtualPlayerData = lazy(() =>
  import("./pages/club/virtualPlayerDataTable/VirtualPlayerData")
);
const CourtCalendar = lazy(() =>
  import("./pages/club/courtDetails/viewCourts/CourtCalendar")
);
// //testing
const WhatsappChatBot = lazy(() =>
  import("./pages/profile/whatsappChatBot/WhatsappChatBot")
);

//lobocancel configurar
const LoboCancelConfig = lazy(() =>
  import("./pages/configurar/LoboCancelConfig")
);

// -----------------------------------------------------
// LoboDomo
// -----------------------------------------------------
const LoboDomo = lazy(() => import("./components/dashboard/LoboDomo"));
const Light = lazy(() => import("./pages/lightstatus/light/Light"));
const Door = lazy(() => import("./pages/doorstatus/door/Door"));
const ClubData = lazy(() => import("./pages/club/clubDataTable/ClubData"));

// -----------------------------------------------------
// LoboEco
// -----------------------------------------------------
const LoboEcoConfig = lazy(() => import("./pages/configurar/LoboEcoConfig"));
const EmbedMiluz = lazy(() => import("./pages/emberdedMiluz/EmbedMiluz"));
const Maintenance = lazy(() => import("./pages/loboEco/Maintenance"));
const ConfigMenu = lazy(() => import("./components/dashboard/LoboEco"));

// -----------------------------------------------------
// Configurations
// -----------------------------------------------------
const Configuraction = lazy(() =>
  import("./pages/configuraction/Configuraction")
);
const ClubConfigForm = lazy(() =>
  import("./pages/configuraction/ClubConfigForm")
);
const ProfileConfig = lazy(() =>
  import("./pages/configuraction/ProfileConfig")
);
const LegalConfig = lazy(() => import("./pages/configuraction/LegalConfig"));

// -----------------------------------------------------
// Users
// -----------------------------------------------------
const Usuarios = lazy(() => import("./pages/usuarios/Usuarios"));
const ClubUsers = lazy(() => import("./pages/profile/userRoles/UserRoles"));
const Roles = lazy(() => import("./pages/profile/roles/Roles"));
const NotifyEmail = lazy(() => import("./components/NotifyEmails"));

// -----------------------------------------------------
// Subscriptions
// -----------------------------------------------------
const Subscription = lazy(() => import("./pages/subscription/Subscription"));

// -----------------------------------------------------
// SR. Lobo Store
// -----------------------------------------------------
const SRLOBOStore = lazy(() => import("./pages/SRLOBOStore"));

// -----------------------------------------------------
// Help & Support
// -----------------------------------------------------
const Help = lazy(() => import("./pages/help/Help"));

const loboecolight = lazy(() => import("./components/dashboard/LoboEcoLights"));
const LoboEcoDashboard = lazy(() =>
  import("./components/dashboard/LoboEcoDashboard")
);

const PdfSigning = lazy(() => import("./pages/Pdf-signing/PdfSigning"));

function App() {
  const { user, loading } = useAuthContext();

  const canManageLights = useAuthorization("manage_lights");
  const canManageDoors = useAuthorization("manage_doors");

  if (loading) return <Loading />;

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route
            index
            element={user ? <Navigate to="/club" /> : <Navigate to="/login" />}
          />
          <Route
            path="login"
            element={
              !user ? (
                <Suspense fallback={<Loading />}>
                  <Login />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="signup"
            element={
              !user ? (
                <Suspense fallback={<Loading />}>
                  <Signup />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="reset-password"
            element={
              !user ? (
                <Suspense fallback={<Loading />}>
                  <ForgotPassword />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="reset-password/:token"
            element={
              !user ? (
                <Suspense fallback={<Loading />}>
                  <ResetPassword />
                </Suspense>
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/"
            element={
              <Suspense fallback={<Loading />}>
                <Layout />
              </Suspense>
            }
          >
            <Route path="club">
              <Route
                index
                element={
                  <ProtectedRoute element={Module} requiredPermission="" />
                }
              ></Route>
              <Route path="data" element={<ClubData />} />
              <Route
                path="court-calendar/:rowId"
                element={
                  <ProtectedRoute
                    element={CourtCalendar}
                    requiredPermission="manage_reservations"
                  />
                }
              />

              <Route
                path="config/lobomatch"
                index
                element={
                  <ProtectedRoute
                    element={WhatsApp}
                    requiredPermission="manage_whatsapp"
                  />
                }
              />

              <Route
                path="config/lobocancel"
                index
                element={
                  <ProtectedRoute
                    element={LoboCancelConfig}
                    requiredPermission="lobocancel"
                  />
                }
              />

              <Route
                path="config/lobolite"
                index
                element={
                  <ProtectedRoute
                    element={WhatsappChatBot}
                    requiredPermission="lobolite"
                  />
                }
              />

              {/* ----------------------------------------------------------------- */}
              {/* ----------------------------LoboDomo----------------------------- */}
              {/* ----------------------------------------------------------------- */}
              <Route path="config/lobodomo" element={<LoboDomo />}>
                <Route
                  index
                  element={
                    <Navigate
                      to={
                        canManageLights
                          ? "lights"
                          : canManageDoors
                          ? "doors"
                          : "club-data"
                      }
                    />
                  }
                />
                <Route
                  path="lights"
                  element={
                    <ProtectedRoute
                      element={Light}
                      requiredPermission="manage_lights"
                    />
                  }
                />
                <Route
                  path="doors"
                  element={
                    <ProtectedRoute
                      element={Door}
                      requiredPermission="manage_doors"
                    />
                  }
                />
                <Route
                  path="club-data"
                  element={
                    <ProtectedRoute element={ClubData} requiredPermission="" />
                  }
                />
                <Route
                  path="manage-codes"
                  element={
                    <ProtectedRoute
                      element={ManagerCode}
                      requiredPermission=""
                    />
                  }
                />
              </Route>

              {/* ----------------------------------------------------------------- */}
              {/* -----------------------LoboEco Dashboard------------------------- */}
              {/* ----------------------------------------------------------------- */}
              <Route
                path="config/loboecolight"
                element={<LoboEcoDashboard />}
              >
                <Route index element={<Navigate to="energy-dashboard" />} />
                <Route
                  path="energy-dashboard"
                  element={
                    <ProtectedRoute
                      element={loboecolight}
                      requiredPermission="manage_miluz"
                    />
                  }
                />
                <Route
                  path="miluz-dashboard"
                  element={
                    <ProtectedRoute
                      element={EmbedMiluz}
                      requiredPermission="manage_miluz"
                    />
                  }
                />
              </Route>

              {/* ----------------------------------------------------------------- */}
              {/* ----------------------------LoboEco------------------------------ */}
              {/* ----------------------------------------------------------------- */}
              <Route path="config/loboeco" element={<LoboEcoConfig />}>
                <Route index element={<Navigate to="config-menu" />} />
                <Route
                  path="config-menu"
                  element={
                    <ProtectedRoute
                      element={ConfigMenu}
                      requiredPermission="manage_miluz"
                    />
                  }
                />
                <Route
                  path="maintenance"
                  element={
                    <ProtectedRoute
                      element={Maintenance}
                      requiredPermission="manage_miluz"
                    />
                  }
                />
              </Route>

              <Route
                path="config/loboxsell"
                element={
                  <ProtectedRoute
                    element={LoboXSell}
                    requiredPermission="loboxsell"
                  />
                }
              />
              <Route
                path="config/loboboost"
                element={
                  <ProtectedRoute
                    element={VirtualPlayerData}
                    requiredPermission="loboboost"
                  />
                }
              />
            </Route>

            {/* ----------------------------------------------------------------- */}
            {/* ----------------------------Configurations----------------------- */}
            {/* ----------------------------------------------------------------- */}
            <Route path="configuraction" element={<Configuraction />}>
              <Route index element={<Navigate to="account-data" />} />
              <Route
                path="account-data"
                element={
                  <ProtectedRoute
                    element={ClubConfigForm}
                    requiredPermission="manage_settings"
                  />
                }
              />
              <Route
                path="profile"
                element={
                  <ProtectedRoute
                    element={ProfileConfig}
                    requiredPermission="manage_settings"
                  />
                }
              />
              <Route
                path="legal"
                element={
                  <ProtectedRoute
                    element={LegalConfig}
                    requiredPermission="manage_settings"
                  />
                }
              />
            </Route>

            {/* ----------------------------------------------------------------- */}
            {/* ----------------------------Users-------------------------------- */}
            {/* ----------------------------------------------------------------- */}
            <Route path="usuarios" element={<Usuarios />}>
              <Route index element={<Navigate to="club-users" />} />
              <Route
                path="club-users"
                element={
                  <ProtectedRoute
                    element={ClubUsers}
                    requiredPermission="users"
                  />
                }
              />
              <Route
                path="roles"
                element={
                  <ProtectedRoute element={Roles} requiredPermission="users" />
                }
              />
            </Route>

            {/* ----------------------------------------------------------------- */}
            {/* ----------------------------Subscriptions------------------------ */}
            {/* ----------------------------------------------------------------- */}
            <Route path="suscripcion">
              <Route
                index
                element={
                  <ProtectedRoute
                    element={Subscription}
                    requiredPermission="billings"
                  />
                }
              />
            </Route>

            {/* ----------------------------------------------------------------- */}
            {/* ----------------------------SR. Lobo Store----------------------- */}
            {/* ----------------------------------------------------------------- */}
            <Route path="sr-lobo-store">
              <Route
                index
                element={
                  <ProtectedRoute
                    element={SRLOBOStore}
                    requiredPermission="billings"
                  />
                }
              />
            </Route>

            {/* ----------------------------------------------------------------- */}
            {/* ----------------------------Help & Support----------------------- */}
            {/* ----------------------------------------------------------------- */}
            <Route path="soporte">
              <Route
                index
                element={
                  <ProtectedRoute element={Help} requiredPermission="" />
                }
              />
            </Route>

            {/* <Route path="light-state">
                <Route
                  index
                  element={
                    <ProtectedRoute
                      element={Light}
                      requiredPermission="manage_lights"
                    />
                  }
                />
              </Route>
              <Route path="doorstate">
                <Route
                  index
                  element={
                    <ProtectedRoute
                      element={Door}
                      requiredPermission="manage_doors"
                    />
                  }
                />
              </Route>
              <Route path="messages">
                <Route
                  index
                  element={
                    <Suspense fallback={<Loading />}>
                      <Message />
                    </Suspense>
                  }
                />
              </Route>
              <Route path="notifications">
                <Route
                  index
                  element={
                    <Suspense fallback={<Loading />}>
                      <Notification />
                    </Suspense>
                  }
                />
              </Route>
              <Route path="manager_codes">
                <Route
                  index
                  element={
                    user ? (
                      <ProtectedRoute
                        element={ManagerCode}
                        requiredPermission="manage_doors"
                      />
                    ) : (
                      <Navigate to="/login" />
                    )
                  }
                />
              </Route>

              <Route path="whatsapp">
                <Route
                  index
                  element={
                    user ? (
                      <ProtectedRoute
                        element={WhatsApp}
                        requiredPermission="manage_whatsapp"
                      />
                    ) : (
                      <Navigate to="/login" />
                    )
                  }
                />
              </Route>

              <Route path="virtual-player">
                <Route
                  index
                  element={
                    <ProtectedRoute
                      element={VirtualPlayerData}
                      // requiredPermission="manage_miluz"
                    />
                  }
                />
              </Route>

              <Route
                path="profile"
                element={<ProtectedRoute element={Profile} />}
              >
                <Route
                  index
                  element={<ProtectedRoute element={ProfileDetails} />}
                />
                <Route path="reset-password" element={<Password />} />
                <Route
                  path="miluz"
                  element={
                    <ProtectedRoute
                      element={MiluzProfile}
                      requiredPermission="manage_miluz"
                    />
                  }
                />
                <Route
                  path="whatsappQR"
                  element={
                    <ProtectedRoute
                      element={WhatsappQR}
                      requiredPermission="manage_whatsapp"
                    />
                  }
                />
                <Route
                  path="roles"
                  element={
                    <ProtectedRoute
                      element={Roles}
                      requiredPermission="manage_settings"
                    />
                  }
                />
                <Route
                  path="user-roles"
                  element={
                    <ProtectedRoute
                      element={UserRoles}
                      requiredPermission="manage_settings"
                    />
                  }
                /> */}
            {/* <Route
                  path="whatsappChatBot"
                  element={
                    <ProtectedRoute
                      element={WhatsappChatBot}
                      requiredPermission="manage_whatsapp"
                    />
                  }
                /> */}
            {/* </Route> */}
          </Route>
          <Route path="/unauthorized" element={<Unauthorized />} />
          {/* Catch-all route for 404 page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
